import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Card, Table, Badge, Button, Spinner, Alert } from 'react-bootstrap';
import { LuArrowLeft, LuCreditCard, LuTruck, LuPackage, LuCheck, LuClock, LuCircleX, LuMapPin } from "react-icons/lu";
import { getMyOrderById, cancelOrder } from '../../redux/reducers/portalOrderSlice';
import moment from 'moment';
import { formatPhoneNumber } from '../../utils/phoneUtils';
import { formatCurrency } from '../../utils/currency';

const PortalOrderDetail = () => {
    const { id, businessId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { currentOrder, loading } = useSelector((state) => state.portalOrder);
    const currency = useSelector((state) => state.portalAuth?.businessData?.jssettings?.currency || 'CAD');

    useEffect(() => {
        dispatch(getMyOrderById(id));
    }, [dispatch, id]);

    const getStatusInfo = (status) => {
        const statuses = {
            'PENDING_PAYMENT': { color: '#f59e0b', icon: LuClock, label: 'Awaiting Payment' },
            'PAID': { color: '#0ea5e9', icon: LuCheck, label: 'Payment Received' },
            'ACCEPTED': { color: '#10b981', icon: LuCheck, label: 'Order Accepted' },
            'PROCESSING': { color: '#6366f1', icon: LuPackage, label: 'Preparing Items' },
            'SHIPPED': { color: '#28B0C4', icon: LuTruck, label: 'On the Way' },
            'DELIVERED': { color: '#10b981', icon: LuCheck, label: 'Delivered' },
            'CANCELLED': { color: '#64748b', icon: LuCircleX, label: 'Cancelled' },
            'REJECTED': { color: '#ef4444', icon: LuCircleX, label: 'Rejected' },
            'COMPLETED': { color: '#10b981', icon: LuCheck, label: 'Completed' },
        };
        return statuses[status] || { color: '#64748b', icon: LuClock, label: status };
    };

    if (loading && !currentOrder) return <div className="text-center py-5"><Spinner animation="border" style={{ color: "#28B0C4" }} /></div>;
    if (!currentOrder) return <div className="text-center py-5">Order not found.</div>;

    const statusInfo = getStatusInfo(currentOrder.status);

    return (
        <div>
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div className="d-flex align-items-center gap-3">
                    <Button variant="light" onClick={() => navigate(`/customer-side/${businessId}/orders`)} className="rounded-3 p-2">
                        <LuArrowLeft size={20} />
                    </Button>
                    <div>
                        <h2 className="fw-bold text-dark mb-0">{currentOrder.orderNumber}</h2>
                        <div className="text-muted small">Placed on {moment(currentOrder.createdAt).format('MMMM D, YYYY at h:mm A')}</div>
                    </div>
                </div>
                
                <Badge className="px-3 py-2 rounded-pill fw-bold" style={{ backgroundColor: `${statusInfo.color}15`, color: statusInfo.color, fontSize: '14px' }}>
                    <statusInfo.icon size={16} className="me-2" />
                    {statusInfo.label}
                </Badge>
            </div>

            <Row className="g-4">
                <Col lg={8}>
                    <Card className="border-0 shadow-sm rounded-4 overflow-hidden mb-4">
                        <Card.Body className="p-0">
                            <Table responsive className="align-middle mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="ps-4 py-3 text-muted small fw-bold">ITEM</th>
                                        <th className="py-3 text-muted small fw-bold text-center">QTY</th>
                                        <th className="py-3 text-muted small fw-bold text-end">PRICE</th>
                                        <th className="pe-4 py-3 text-muted small fw-bold text-end">TOTAL</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentOrder.items?.map((item, idx) => (
                                        <tr key={idx}>
                                            <td className="ps-4 py-3">
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="rounded-3 bg-light d-flex align-items-center justify-content-center" style={{ width: '45px', height: '45px', flexShrink: 0 }}>
                                                        {item.productImage ? (
                                                            <img src={item.productImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} className="rounded-3" />
                                                        ) : (
                                                            <LuPackage size={20} className="text-muted opacity-50" />
                                                        )}
                                                    </div>
                                                    <div className="fw-bold text-dark small">{item.productName}</div>
                                                </div>
                                            </td>
                                            <td className="py-3 text-center">{item.quantity}</td>
                                            <td className="py-3 text-end text-muted small">{formatCurrency(item.unitPrice, currency)}</td>
                                            <td className="pe-4 py-3 text-end fw-bold">{formatCurrency(item.lineTotal, currency)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                        <Card.Footer className="bg-white border-0 p-4 border-top">
                            <Row className="justify-content-end">
                                <Col md={5}>
                                    <div className="d-flex justify-content-between mb-2 text-muted">
                                        <span>Subtotal</span>
                                        <span>{formatCurrency(currentOrder.totals?.subtotal, currency)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2 text-muted">
                                        <span>Tax (13%)</span>
                                        <span>{formatCurrency(currentOrder.totals?.taxAmount || 0, currency)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-3 text-muted">
                                        <span>Shipping</span>
                                        <span className="text-success small fw-semibold">Business will update after delivery</span>
                                    </div>
                                    <div className="d-flex justify-content-between pt-2 border-top">
                                        <h5 className="fw-bold">Total</h5>
                                        <h5 className="fw-bold" style={{ color: "#28B0C4" }}>{formatCurrency(currentOrder.totals?.total, currency)}</h5>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Footer>
                    </Card>

                    <Card className="border-0 shadow-sm rounded-4">
                        <Card.Body className="p-4">
                            <div className="d-flex align-items-center gap-2 mb-4">
                                <LuMapPin size={24} style={{ color: "#28B0C4" }} />
                                <h5 className="fw-bold mb-0">Delivery Details</h5>
                            </div>
                            <div className="bg-light rounded-4 p-4">
                                <div className="fw-bold text-dark mb-1">{currentOrder.deliveryAddress?.recipientName}</div>
                                <div className="text-muted">
                                    {currentOrder.deliveryAddress?.street}<br />
                                    {currentOrder.deliveryAddress?.city}, {currentOrder.deliveryAddress?.state} {currentOrder.deliveryAddress?.zipCode}<br />
                                    {currentOrder.deliveryAddress?.country}
                                </div>
                                <div className="text-muted mt-2">Phone: {formatPhoneNumber(currentOrder.deliveryAddress?.phone)}</div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={4}>
                    <Card className="border-0 shadow-sm rounded-4 mb-4">
                        <Card.Body className="p-4">
                            <div className="d-flex align-items-center gap-2 mb-4">
                                <LuCreditCard size={24} style={{ color: "#28B0C4" }} />
                                <h5 className="fw-bold mb-0">Payment Status</h5>
                            </div>
                            
                            {currentOrder.status === 'PENDING_PAYMENT' ? (
                                <div>
                                    <Alert variant="warning" className="rounded-3 border-0 small mb-4">
                                        Payment is required to process your order.
                                    </Alert>
                                    <div className="d-flex justify-content-between mb-3 text-muted">
                                        <span>Amount Due</span>
                                        <span className="fw-bold text-dark">{formatCurrency(currentOrder.totals?.total, currency)}</span>
                                    </div>
                                    {currentOrder.invoiceId?.paymentUrl && (
                                        <Button 
                                            href={currentOrder.invoiceId.paymentUrl} 
                                            className="w-100 rounded-3 py-2 fw-bold text-white border-0 mb-3"
                                            style={{ background: "#28B0C4" }}
                                        >
                                            Pay Now
                                        </Button>
                                    )}
                                    <Button variant="outline-danger" className="w-100 rounded-3 py-2 small" onClick={() => dispatch(cancelOrder(currentOrder._id))}>
                                        Cancel Order
                                    </Button>
                                </div>
                            ) : (
                                <div>
                                    <div className="d-flex align-items-center gap-3 text-success mb-3 p-3 bg-success-subtle rounded-3">
                                        <LuCheck size={24} />
                                        <div>
                                            <div className="fw-bold">Payment Received</div>
                                            <div className="extra-small opacity-75">{moment(currentOrder.paidAt || currentOrder.updatedAt).format('MMM D, YYYY')}</div>
                                        </div>
                                    </div>
                                    <Button as={Link} to={`/customer-side/${businessId}/invoices/${currentOrder.invoiceId?._id || currentOrder.invoiceId}`} variant="light" className="w-100 rounded-3 py-2 text-dark small">
                                        View Invoice
                                    </Button>
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {currentOrder.shipmentId && (
                        <Card className="border-0 shadow-sm rounded-4 mb-4">
                            <Card.Body className="p-4">
                                <div className="d-flex align-items-center gap-2 mb-4">
                                    <LuTruck size={24} style={{ color: "#28B0C4" }} />
                                    <h5 className="fw-bold mb-0">Shipment Status</h5>
                                </div>
                                <div className="mb-3">
                                    <div className="text-muted small">Tracking Number</div>
                                    <div className="fw-bold text-dark">{currentOrder.shipmentId.trackingCode}</div>
                                </div>
                                <div className="mb-4">
                                    <div className="text-muted small">Status</div>
                                    <div className="fw-bold text-dark">{currentOrder.shipmentId.status}</div>
                                </div>
                                <Button 
                                    as={Link} 
                                    to={`/track/${currentOrder.shipmentId.trackingCode}`} 
                                    className="w-100 rounded-3 py-2 text-white border-0"
                                    style={{ background: "#28B0C4" }}
                                >
                                    Track Shipment
                                </Button>
                            </Card.Body>
                        </Card>
                    )}

                    <Card className="border-0 shadow-sm rounded-4">
                        <Card.Body className="p-4">
                            <h5 className="fw-bold mb-4">Order Timeline</h5>
                            <div className="timeline-v2">
                                {currentOrder.timeline?.slice().reverse().map((step, idx) => (
                                    <div key={idx} className="timeline-item-v2 position-relative ps-4 pb-4">
                                        <div className="timeline-point position-absolute start-0 rounded-circle bg-white border-2" style={{ width: '12px', height: '12px', borderColor: idx === 0 ? "#28B0C4" : "#e2e8f0", marginTop: '6px' }}></div>
                                        <div className={`fw-bold small ${idx === 0 ? 'text-dark' : 'text-muted'}`}>{step.status.replace('_', ' ')}</div>
                                        <div className="text-muted extra-small">{step.note}</div>
                                        <div className="text-muted extra-small opacity-75">{moment(step.timestamp).fromNow()}</div>
                                    </div>
                                ))}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default PortalOrderDetail;
